import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireEnterpriseAdmin?: boolean;
  requireRole?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireEnterpriseAdmin = false,
  requireRole
}) => {
  const { user, profile, loading, hasRole, isAdmin, isEnterpriseAdmin } = useAuth();
  const location = useLocation();
  const [waitingForProfile, setWaitingForProfile] = useState(false);
  const [profileWaitTimeout, setProfileWaitTimeout] = useState(false);

  // Track if we're waiting for a profile to be created
  useEffect(() => {
    if (user && !profile && !loading) {
      // Wait 1 second before showing "Setting up your profile..."
      // This prevents showing it during normal profile fetch on page reload
      const delayTimeout = setTimeout(() => {
        // TODO: GA4_TRACKING - User exists but no profile after 1s - showing setup message
        setWaitingForProfile(true);
      }, 1000);

      // Set a timeout to prevent infinite waiting (10 seconds total)
      const maxTimeout = setTimeout(() => {
        // TODO: GA4_TRACKING - Profile wait timeout - profile may not exist
        setProfileWaitTimeout(true);
      }, 10000);

      return () => {
        clearTimeout(delayTimeout);
        clearTimeout(maxTimeout);
      };
    } else {
      setWaitingForProfile(false);
      setProfileWaitTimeout(false);
    }
  }, [user, profile, loading]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" state={{ from: location }} replace />;
  }

  // Show "Setting up your profile..." if user exists but profile doesn't (and we haven't timed out)
  if (waitingForProfile && !profileWaitTimeout && location.pathname !== '/onboarding') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Setting up your profile...</p>
        </div>
      </div>
    );
  }

  // If we timed out waiting for profile, show error
  if (profileWaitTimeout && !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <p className="text-destructive mb-4">Profile setup failed or is taking too long.</p>
          <p className="text-muted-foreground text-sm">Please contact support or try logging out and back in.</p>
        </div>
      </div>
    );
  }

  // Redirect to onboarding if user hasn't completed it (except if already on onboarding page)
  if (profile && !profile.onboarding_completed && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  // Guard: if a role-based check is required but profile/roles haven't loaded yet,
  // keep showing the spinner rather than evaluating isAdmin() against an empty
  // roles array. This prevents a false redirect to "/" during the async fetch window
  // (AuthContext sets loading=false immediately but fetches the profile async).
  const requiresRoleCheck = requireAdmin || requireEnterpriseAdmin || !!requireRole;
  if (requiresRoleCheck && !profile && !profileWaitTimeout) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (requireAdmin && !isAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireEnterpriseAdmin && !isEnterpriseAdmin()) {
    return <Navigate to="/" replace />;
  }

  if (requireRole && !hasRole(requireRole)) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;